/**
 * sqlite3mt client WAL commit helpers
 *
 * Mechanical extraction from §96 §11.1.2–§11.1.3.
 *
 * This module owns the Client Worker side of the WAL commit path:
 * - acquire WAL_WRITE without gate-slip (§11.1.2)
 * - copy the just-committed transaction pages out of SAB2 (§11.1.3)
 * - derive a page-count-aware delta from the commit frame metadata
 * - publish COMMIT_POST_SEQ before posting wal-commit-sync
 * - wait for Control Worker ACK or fatal/stale generation
 * - release WAL_WRITE / WAL_WRITE_HOLDERS exactly once in finally
 */

import {
    COMMIT_ACK_RC_OK,
    publishCommitPosted,
    runtimeEpochMatches,
    waitForCommitAckOrFatal,
    waitUntilControlReadyOrFatal,
} from './control'
import {
    DEFAULT_PAGE_SIZE,
    Sab4Scalar,
    sab2DataView,
    sab2SizeView,
    sab4ScalarView,
} from './sab-layout'
import { isWalSnapshotUnstableError } from './shm-ops'
import {
    WAL_FRAME_HEADER_SIZE,
    WAL_HEADER_SIZE,
    walFrameOffset,
    type ClientWorkerConnState,
    type ExtractedCommitPages,
    type PageHash,
    type Pgno,
    type WalCommitSyncMessage,
    type WalSnapshot,
} from './types'
import { parseWalHeader } from './wal-checksum'

export const SQLITE_OK = 0
export const SQLITE_BUSY = 5
export const SQLITE_IOERR = 10

export const INVALID_COMMIT_FRAME_RANGE = 'INVALID_COMMIT_FRAME_RANGE'
export const TRUNCATED_WAL_COMMIT_RANGE = 'TRUNCATED_WAL_COMMIT_RANGE'
export const PENDING_COMMIT_FRAME_NOT_COMMIT = 'PENDING_COMMIT_FRAME_NOT_COMMIT'
export const INVALID_COMMIT_PAGE_NUMBER = 'INVALID_COMMIT_PAGE_NUMBER'

export interface AcquireWalWriteLockArgs {
    connState: ClientWorkerConnState
    sab4: SharedArrayBuffer
    tryAcquireWalWriteLock(): boolean
    enterFatalIoModeForConnection(): void
}

export interface PrepareCommitWriteBeforeFirstWalByteArgs {
    connState: ClientWorkerConnState
    sab4: SharedArrayBuffer
    readWalSnapshotStable(): WalSnapshot
    enterFatalIoModeForConnection(): void
}

export interface BuildWalCommitSyncMessageArgs {
    connState: Pick<
        ClientWorkerConnState,
        | 'attachedRuntimeEpoch'
        | 'admittedCheckpointGateSeq'
        | 'admittedWriteGateSeq'
        | 'connId'
        | 'nextCommitReqId'
        | 'pendingCommitFrame'
        | 'txStartFrame'
    >
    sab2: SharedArrayBuffer
    hashPage(pageData: Uint8Array): PageHash
    walSnapshot?: Pick<WalSnapshot, 'salt0' | 'salt1'>
    pageSize?: number
}

export interface ReleaseWalWriteAfterCommitSyncArgs extends BuildWalCommitSyncMessageArgs {
    connState: ClientWorkerConnState
    sab4: SharedArrayBuffer
    readWalSnapshotStable(): WalSnapshot
    postMessageToControl(message: WalCommitSyncMessage, transfer: ArrayBuffer[]): void
    enterFatalIoModeForConnection(): void
    releaseWalWriteLock(): void
}

export interface BuiltWalCommitSyncMessage {
    message: WalCommitSyncMessage
    reqId: bigint
    transfer: ArrayBuffer[]
}

/**
 * Mirror SQLite's WAL_WRITE ownership in SAB4 without assuming that every
 * slot-0 acquire is a user commit. SQLite also takes WAL_WRITE during WAL
 * recovery/header repair, and those paths may legitimately observe an
 * unstable wal-index. Commit-specific gate checks and txStartFrame
 * initialization therefore happen later, immediately before the first WAL
 * byte is copied into SAB2.
 */
export function acquireWalWriteLock({
    connState,
    sab4,
    tryAcquireWalWriteLock,
    enterFatalIoModeForConnection,
}: AcquireWalWriteLockArgs): number {
    const scalar = sab4ScalarView(sab4)

    if (waitUntilControlReadyOrFatal(sab4, connState.attachedRuntimeEpoch) === 'FATAL') {
        enterFatalIoMode(connState, enterFatalIoModeForConnection)
        return SQLITE_IOERR
    }

    if (!tryAcquireWalWriteLock()) {
        return SQLITE_BUSY
    }

    if (!connState.holdsWalWrite) {
        Atomics.add(scalar, Sab4Scalar.WAL_WRITE_HOLDERS, 1)
    }

    connState.holdsWalWrite = true
    connState.admittedWriteGateSeq = null
    connState.admittedCheckpointGateSeq = null
    connState.pendingCommitFrame = 0
    connState.txStartFrame = 0
    return SQLITE_OK
}

/**
 * Initialize commit-specific state after WAL_WRITE is already held but before
 * the first WAL byte is copied into SAB2. This preserves the gate-slip
 * invariant without blocking SQLite's internal WAL recovery paths, which also
 * take slot 0 but are not commits.
 */
export function prepareCommitWriteBeforeFirstWalByte({
    connState,
    sab4,
    readWalSnapshotStable,
    enterFatalIoModeForConnection,
}: PrepareCommitWriteBeforeFirstWalByteArgs): number {
    const scalar = sab4ScalarView(sab4)

    if (!connState.holdsWalWrite) {
        return SQLITE_IOERR
    }

    if (connState.txStartFrame > 0) {
        return SQLITE_OK
    }

    if (waitUntilControlReadyOrFatal(sab4, connState.attachedRuntimeEpoch) === 'FATAL') {
        enterFatalIoMode(connState, enterFatalIoModeForConnection)
        return SQLITE_IOERR
    }

    if (Atomics.load(scalar, Sab4Scalar.FATAL_FLAG) !== 0
        || !runtimeEpochMatches(sab4, connState.attachedRuntimeEpoch)) {
        enterFatalIoMode(connState, enterFatalIoModeForConnection)
        return SQLITE_IOERR
    }

    const admittedWriteGateSeq = Atomics.load(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ)
    const admittedCheckpointGateSeq = Atomics.load(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ)

    if (Atomics.load(scalar, Sab4Scalar.WRITE_GATE_CLOSED) !== 0) {
        return SQLITE_BUSY
    }

    if (Atomics.load(scalar, Sab4Scalar.CHECKPOINT_GATE_CLOSED) !== 0) {
        return SQLITE_BUSY
    }

    if (
        Atomics.load(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ) !== admittedWriteGateSeq
        || Atomics.load(scalar, Sab4Scalar.CHECKPOINT_GATE_WAITSEQ) !== admittedCheckpointGateSeq
    ) {
        return SQLITE_BUSY
    }

    try {
        const snap = readWalSnapshotStable()
        connState.txStartFrame = snap.mxFrame + 1
        connState.admittedWriteGateSeq = admittedWriteGateSeq
        connState.admittedCheckpointGateSeq = admittedCheckpointGateSeq
        connState.pendingCommitFrame = 0
        return SQLITE_OK
    } catch (error) {
        if (isWalSnapshotUnstableError(error)) {
            return SQLITE_BUSY
        }
        enterFatalIoMode(connState, enterFatalIoModeForConnection)
        return SQLITE_IOERR
    }
}

function readU32BE(data: Uint8Array, offset: number): number {
    return (
        ((data[offset]! << 24) >>> 0)
        | (data[offset + 1]! << 16)
        | (data[offset + 2]! << 8)
        | data[offset + 3]!
    ) >>> 0
}

function readWalHeaderSaltPair(sab2: SharedArrayBuffer): Pick<WalSnapshot, 'salt0' | 'salt1'> {
    const walSize = Atomics.load(sab2SizeView(sab2), 0)
    if (walSize < WAL_HEADER_SIZE) {
        throw new RangeError('WAL header is not fully present in SAB2')
    }

    const walHeader = parseWalHeader(sab2DataView(sab2).subarray(0, walSize))
    if (walHeader == null) {
        throw new RangeError('WAL header is invalid in SAB2')
    }

    return {
        salt0: walHeader.salt1,
        salt1: walHeader.salt2,
    }
}

/**
 * Copy the committed page images for a single WAL transaction out of SAB2.
 *
 * Frames are scanned backward so the first copy seen for a pgno is the final
 * page image for that transaction. The commit frame's nPage field is the only
 * source of logical EOF for the returned delta.
 */
export function extractCommittedPagesAndCommitSizeBackwardCopy(
    sab2: SharedArrayBuffer,
    txStartFrame: number,
    pendingCommitFrame: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): ExtractedCommitPages {
    if (!Number.isInteger(txStartFrame)
        || !Number.isInteger(pendingCommitFrame)
        || txStartFrame < 1
        || pendingCommitFrame < txStartFrame) {
        throw new RangeError(INVALID_COMMIT_FRAME_RANGE)
    }

    const walSize = Atomics.load(sab2SizeView(sab2), 0)
    const walBytes = sab2DataView(sab2).subarray(0, walSize)
    const frameSize = WAL_FRAME_HEADER_SIZE + pageSize
    const commitFrameOffset = walFrameOffset(pendingCommitFrame, pageSize)

    if (walBytes.length < WAL_HEADER_SIZE || commitFrameOffset + frameSize > walBytes.length) {
        throw new RangeError(TRUNCATED_WAL_COMMIT_RANGE)
    }

    const commitDbSizePages = readU32BE(walBytes, commitFrameOffset + 4)
    if (commitDbSizePages <= 0) {
        throw new Error(PENDING_COMMIT_FRAME_NOT_COMMIT)
    }

    const pages = new Map<Pgno, Uint8Array>()

    for (let frameNo = pendingCommitFrame; frameNo >= txStartFrame; frameNo -= 1) {
        const frameOffset = walFrameOffset(frameNo, pageSize)
        if (frameOffset + frameSize > walBytes.length) {
            throw new RangeError(TRUNCATED_WAL_COMMIT_RANGE)
        }

        const pgno = readU32BE(walBytes, frameOffset)
        if (pgno < 1) {
            throw new Error(INVALID_COMMIT_PAGE_NUMBER)
        }

        const pageNo = pgno as Pgno
        if (pages.has(pageNo)) {
            continue
        }

        pages.set(
            pageNo,
            walBytes.slice(frameOffset + WAL_FRAME_HEADER_SIZE, frameOffset + frameSize)
        )
    }

    return {
        pages,
        commitDbSizePages,
    }
}

export function buildWalCommitSyncMessage({
    connState,
    sab2,
    hashPage,
    walSnapshot,
    pageSize = DEFAULT_PAGE_SIZE,
}: BuildWalCommitSyncMessageArgs): BuiltWalCommitSyncMessage {
    const { pages, commitDbSizePages } = extractCommittedPagesAndCommitSizeBackwardCopy(
        sab2,
        connState.txStartFrame,
        connState.pendingCommitFrame,
        pageSize
    )

    const pageHashes = new Map<Pgno, PageHash>()
    const blobs = new Map<PageHash, Uint8Array>()
    for (const [pgno, dataCopy] of pages) {
        const hash = hashPage(dataCopy)
        blobs.set(hash, dataCopy)
        pageHashes.set(pgno, hash)
    }

    const walSalt = walSnapshot ?? readWalHeaderSaltPair(sab2)
    const reqId = connState.nextCommitReqId + 1n
    connState.nextCommitReqId = reqId

    return {
        message: {
            type: 'wal-commit-sync',
            runtimeEpoch: connState.attachedRuntimeEpoch,
            connId: connState.connId,
            reqId,
            admittedWriteGateSeq: connState.admittedWriteGateSeq ?? undefined,
            admittedCheckpointGateSeq: connState.admittedCheckpointGateSeq ?? undefined,
            walSalt0: walSalt.salt0,
            walSalt1: walSalt.salt1,
            frameMax: connState.pendingCommitFrame,
            delta: {
                pageHashes,
                postPageCount: commitDbSizePages,
            },
            blobs,
        },
        reqId,
        transfer: Array.from(blobs.values(), bytes => bytes.buffer as ArrayBuffer),
    }
}

export function releaseWalWriteAfterCommitSync({
    connState,
    sab2,
    sab4,
    hashPage,
    readWalSnapshotStable,
    postMessageToControl,
    enterFatalIoModeForConnection,
    releaseWalWriteLock,
    pageSize = DEFAULT_PAGE_SIZE,
}: ReleaseWalWriteAfterCommitSyncArgs): number {
    try {
        if (connState.pendingCommitFrame > 0) {
            const liveWalSnapshot = readWalSnapshotStable()
            const { message, reqId, transfer } = buildWalCommitSyncMessage({
                connState,
                sab2,
                hashPage,
                walSnapshot: liveWalSnapshot,
                pageSize,
            })

            publishCommitPosted(sab4, connState.connId, reqId)
            postMessageToControl(message, transfer)

            const ack = waitForCommitAckOrFatal(
                sab4,
                connState.attachedRuntimeEpoch,
                connState.connId,
                reqId
            )
            if (ack.kind === 'FATAL' || ack.rc !== COMMIT_ACK_RC_OK) {
                enterFatalIoMode(connState, enterFatalIoModeForConnection)
                return SQLITE_IOERR
            }

            connState.pendingCommitFrame = 0
        }

        return SQLITE_OK
    } catch {
        enterFatalIoMode(connState, enterFatalIoModeForConnection)
        return SQLITE_IOERR
    } finally {
        connState.admittedWriteGateSeq = null
        connState.admittedCheckpointGateSeq = null
        releaseWalWriteIfHeld(connState, sab4, releaseWalWriteLock)
    }
}

export function releaseWalWriteIfHeld(
    connState: Pick<ClientWorkerConnState, 'holdsWalWrite'>,
    sab4: SharedArrayBuffer,
    releaseWalWriteLock: () => void
): void {
    if (!connState.holdsWalWrite) {
        return
    }

    connState.holdsWalWrite = false
    Atomics.sub(sab4ScalarView(sab4), Sab4Scalar.WAL_WRITE_HOLDERS, 1)
    releaseWalWriteLock()
}

function enterFatalIoMode(
    connState: Pick<ClientWorkerConnState, 'fatalIoMode'>,
    enterFatalIoModeForConnection: () => void
): void {
    connState.fatalIoMode = true
    enterFatalIoModeForConnection()
}
